import SimplePortableText from '@components/simple-portable-text'
import { SanitySimplePortableText } from '@data/sanity/queries/types/content'
import {
  SanityProductFragment,
  SanityProductOption,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { SanityShoppingInfo } from '@data/sanity/queries/types/site'
import { ProductOptionItem, sizeOptionNames } from '@lib/product-options'
import { LayoutContext } from '@lib/site-context'
import { useContext, useMemo, useRef } from 'react'
import { useResizeObserver } from 'usehooks-ts'
import ProductActions from './product-actions'
import ProductHeroChips from './product-hero-chips'
import ProductHeroContentPosition from './product-hero-content-position'
import ProductOptionList from './product-option-list'
import ProductPrice from './product-price'
import ProductShoppingInfo from './product-shopping-info'

interface ProductHeroContentProps {
  title: string
  description?: SanitySimplePortableText
  shoppingInfo?: SanityShoppingInfo
  product: SanityProductFragment
  variant: SanityProductVariantFragment
  options: SanityProductOption[]
  productPrice: number
  productComparePrice?: number
  isEveryOptionSelected?: boolean
  productOptionsList: ProductOptionItem[][]
  productOptionLabels: string[]
  isPreorderActive: boolean
  preorderCountdownTarget?: string
  preorderDeliveryDate?: string
  isAvailabilityCountdownActive?: boolean
  availabilityCountdownTarget?: string
  onSelect: (optionName: string, optionValue: string) => void
  onPreview: (optionName: string, optionValue?: string) => void
}

const ProductHeroContent = ({
  title,
  description,
  shoppingInfo,
  product,
  variant,
  options,
  productPrice,
  productComparePrice,
  isEveryOptionSelected,
  productOptionsList,
  productOptionLabels,
  isPreorderActive,
  preorderCountdownTarget,
  preorderDeliveryDate,
  isAvailabilityCountdownActive,
  availabilityCountdownTarget,
  onSelect,
  onPreview,
}: ProductHeroContentProps) => {
  const { headerHeight, breadcrumbsHeight } = useContext(LayoutContext)

  const titleRef = useRef<HTMLDivElement | null>(null)

  const titleRectangle = useResizeObserver({
    ref: titleRef,
  })

  const titleHeight = useMemo(() => {
    return titleRectangle?.height ?? 0
  }, [titleRectangle])

  return (
    <div className="w-full sm:max-w-xl mx-0 bg-pageBG">
      <ProductHeroContentPosition>
        <div className="flex flex-col gap-y-5 pb-12">
          {!!variant && (
            <div
              ref={titleRef}
              className="sm:sticky bg-pageBG z-[2]"
              style={{
                top: headerHeight + breadcrumbsHeight,
              }}
            >
              <div className="sm:pt-12 pb-2 px-3 space-y-1">
                <div className="flex items-start gap-x-2 gap-y-1">
                  <h2 className="py-0.5">{title}</h2>

                  <ProductHeroChips
                    gender={product.gender}
                    inStock={
                      isEveryOptionSelected ? variant.inStock : product.inStock
                    }
                    lowStock={
                      isEveryOptionSelected
                        ? variant.lowStock
                        : product.lowStock
                    }
                    productPrice={productPrice}
                    productComparePrice={productComparePrice}
                    isPreorderActive={isPreorderActive}
                    isAvailabilityCountdownActive={
                      isAvailabilityCountdownActive
                    }
                  />
                </div>

                <ProductPrice
                  price={variant.price ?? productPrice}
                  comparePrice={variant.comparePrice ?? productComparePrice}
                  className="text-sm"
                />
              </div>
            </div>
          )}

          {options.length > 0 && (
            <div className="flex flex-col gap-y-6 py-2 px-3">
              {options.map((option, index) => {
                if (
                  isAvailabilityCountdownActive &&
                  sizeOptionNames.includes(option.name)
                ) {
                  return null
                }

                return (
                  <ProductOptionList
                    key={`${option.name}-${index}`}
                    productOptions={productOptionsList[index]}
                    label={productOptionLabels[index]}
                    onSelect={(optionValue) => {
                      onSelect(option.name, optionValue)
                    }}
                    onPreview={(optionValue) => {
                      onPreview(option.name, optionValue)
                    }}
                  />
                )
              })}
            </div>
          )}

          <div
            className="sm:sticky py-2 px-3 bg-pageBG z-[3]"
            style={{
              top: headerHeight + breadcrumbsHeight + titleHeight,
            }}
          >
            <ProductActions
              title={title}
              product={product}
              variant={variant}
              isPreorderActive={isPreorderActive}
              preorderCountdownTarget={preorderCountdownTarget}
              preorderDeliveryDate={preorderDeliveryDate}
              isAvailabilityCountdownActive={isAvailabilityCountdownActive}
              availabilityCountdownTarget={availabilityCountdownTarget}
              disabled={!isEveryOptionSelected}
            />
          </div>

          <div className="flex flex-col gap-y-6 px-3">
            {!!description && (
              <SimplePortableText content={description} className="rc" />
            )}

            <ProductShoppingInfo shoppingInfo={shoppingInfo} />
          </div>
        </div>
      </ProductHeroContentPosition>
    </div>
  )
}

export default ProductHeroContent
